import { useState, lazy, Suspense, useEffect, useRef } from "react"

import './index.css'
import Loading from "./Loading";
import { IQuestion } from "../../interfaces/Question";
import { days, intervals } from "../../constants";
import { useSelector } from "react-redux";
import PoweredBy from "../PoweredBy";
import Audiov4 from "./Audiov4";

const Text = lazy(() => import('./Text/'));
const Phone = lazy(() => import('./Phone/'));
const Upload = lazy(() => import('./Upload/'));
const Calendar = lazy(() => import('./Calendar/'));
const Number = lazy(() => import('./Number/'));

// FIXME: handle AI as small only
const textInputComp = ['name', 'statement', 'question', 'location', 'button', 'multi_select', 'email', 'smart_question', 'live_chat', 'image', 'video', 'contact', 'rating', 'range', 'AI', 'live_chat', 'ai'];
const phoneInputComp = ['phone'];
const numberInputComp = ['number'];
const uploadInputComp = ['upload', 'file'];
const calendarInputComp = ['appointment'];
const dateTimeInputComp = ['date'];

type Days = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

export default function UserInputs(props: any) {
  const [lastMessageType, setLastMessageType] = useState('text');
  const [currentQuestion, setCurrentQuestion] = useState<IQuestion>();
  const [internet, setInternet] = useState<'initial' | 'offline' | 'online'>('initial');
  const prevInternetRef = useRef(internet);
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState('document');
  const [shouldShowUpload, setShouldShowUpload] = useState(false);

  const messages = useSelector((state: any) => state?.messages);
  const environment = useSelector((state: any) => state?.environment);
  const botHandler = useSelector((state: any) => state?.botHandler);


  useEffect(() => {
    if (messages?.length) {
      const activeQuestion = environment?.activeQuestion;
      setCurrentQuestion(activeQuestion);
      setLastMessageType(activeQuestion?.type);
    }

    if (uploadInputComp.includes(lastMessageType)) {
      setShouldShowUpload(true);
    }
  }, [messages, environment, lastMessageType]);


  /**
   * UseEffect to handle internet status
   */

  // Use a ref to track the previous value of `internet`


  useEffect(() => {
    let timer: any;

    const setInitial = () => setInternet("initial");

    if (environment.socketDisconnected) {
      setInternet("offline");
      // Update the ref after the effect runs
      prevInternetRef.current = 'offline';
    } else if (environment.socketDisconnected === false) {
      // false equality is important for null exclusion

      const prevInternet = prevInternetRef.current;

      if (prevInternet === 'offline') {
        setInternet("online");
        // Update the ref after the effect runs
        prevInternetRef.current = 'online';
        timer = setTimeout(setInitial, 3000);
      } else {
        setInternet("initial");
        // Update the ref after the effect runs
        prevInternetRef.current = 'initial';
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [environment.socketDisconnected]);

  const getMediaType = (mediaType: any): string => {
    const { type } = mediaType || { type: '' };

    if (type.includes('image')) {
      return 'image';
    } else if (type.includes('video')) {
      return 'video';
    } else if (type.includes('application') || type.includes('text')) {
      return 'document';
    } else if (type.includes('audio')) {
      return 'audio';
    }

    return ''
  }

  const getAllowedMediaValues = (mediaType: "video" | "image" | "document" | "audio"): string => {
    switch (mediaType) {
      case 'image':
        return '.jpg, .jpeg, .png, .gif, .bmp, .svg, .webp';
      case 'video':
        return '.mp4, .avi, .mov, .wmv, .flv, .webm';
      case 'audio':
        return '.mp3, .wav, .aac, .flac, .ogg, .m4a, .wma, .aiff';
      case 'document':
        return '.pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .rtf, .odt, .csv, .html, .htm';
      default:
        return '';
    }
  }

  useEffect(() => {
    const handleDragOver = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragLeave = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (!environment.liveChat) {
        return false;
      }

      let mediaOptions = { blob: null, mediaType: '', mediaName: '', mimetype: '' };
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const mediaItem = e.dataTransfer.files[0];
        const mediaType = getMediaType(mediaItem);

        if (!mediaType) {
          return false;
        }

        mediaOptions = {
          blob: mediaItem, mediaType: mediaType,
          mediaName: mediaItem.name, mimetype: mediaItem.type
        };

        const allowedExtensions = getAllowedMediaValues(mediaOptions.mediaType as any);
        const mediaExtension = (mediaOptions.mediaName || '').split('.').pop() as string;
        if (!allowedExtensions.includes(mediaExtension)) {
          return;
        }

        setFileType(mediaOptions.mediaType);
        setFile(e.dataTransfer.files[0]);
      }
    };

    const handlePasteEvent = (event: any) => {
      const items = (event.clipboardData || event?.originalEvent?.clipboardData)?.items;
      let mediaOptions = { blob: null, mediaType: '', mediaName: '', mimetype: '' };

      for (const item of items) {
        const mediaType = getMediaType(item);

        if (!mediaType) {
          return false;
        }

        mediaOptions = {
          blob: item.getAsFile(), mediaType: mediaType,
          mediaName: item.getAsFile()?.name, mimetype: item.getAsFile()?.type
        };
      }

      const allowedExtensions = getAllowedMediaValues(mediaOptions.mediaType as any);
      const mediaExtension = (mediaOptions.mediaName || '').split('.').pop() as string;
      if (!allowedExtensions.includes(mediaExtension)) {
        return;
      }

      setFileType(mediaOptions.mediaType);
      setFile(mediaOptions.blob);
    }

    // Add global event listeners
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    window.addEventListener('paste', handlePasteEvent);

    return () => {
      // Clean up event listeners on component unmount
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      window.removeEventListener('paste', handlePasteEvent);
    };
  }, [environment.liveChat]);

  return (
    <div className="relative bg-white rounded-xl">
      {
        botHandler === 'AGENT' || environment.liveChat
          ? <Suspense fallback={<Loading />}>
            {
              (file || shouldShowUpload)
                ? <Upload showChatWindowOptions={props.showChatWindowOptions}
                  dragFile={file}
                  setDragFile={setFile}
                  mediaType={fileType}
                  setShouldShowUpload={setShouldShowUpload}
                  shouldShowUpload={shouldShowUpload} />
                : <Text message={lastMessageType} 
                showChatWindowOptions={props?.showChatWindowOptions}
                setShouldShowUpload={setShouldShowUpload} />
            }
          </Suspense>
          : <>
            {/* Calendar comp for appointment component */}
            {
              calendarInputComp.includes(lastMessageType) &&
              <Suspense fallback={<Loading />}>
                <Calendar
                  text={currentQuestion?.label}
                  min={new Date()}
                  max={new Date(
                    currentQuestion?.appointment?.futureDays
                      ? new Date().setDate(new Date().getDate() + currentQuestion.appointment.futureDays)
                      : new Date().setDate(new Date().getDate() + 30)
                  )}
                  availableDays={currentQuestion?.appointment?.availableDays.map((d: Days) => days[d])}
                  startTime={currentQuestion?.appointment?.startTime}
                  endTime={currentQuestion?.appointment?.endTime}
                  slotInterval={intervals[currentQuestion?.appointment?.interval || 30] || 'half'}
                  slots={true}
                  type="appointment"
                  showChatWindowOptions={props.showChatWindowOptions}
                  disablePast={true}
                />

              </Suspense>
            }

            {/* Calendar comp for date component */}
            {
              dateTimeInputComp.includes(lastMessageType) &&
              <Suspense fallback={<Loading />}>
                <Calendar
                  text={currentQuestion?.label}
                  time={currentQuestion?.showTime}
                  showTime={currentQuestion?.showTime}
                  availableDays={[0, 1, 2, 3, 4, 5, 6]}
                  type="date"
                  showChatWindowOptions={props.showChatWindowOptions}
                  disablePast={false}
                  min=''
                  max=''
                />
              </Suspense>
            }

            {
              phoneInputComp.includes(lastMessageType) &&
              <div id='ele-374' className="relative">
                <Suspense fallback={<Loading />}>
                  <Phone
                    phoneValidation={environment.activeQuestion ? environment.activeQuestion.phoneValidation : {}}
                    defaultDialCode={(environment.activeQuestion && environment.activeQuestion.defaultDialCode) ? (environment.activeQuestion.defaultDialCode.code || 'IN') : ''}
                    showChatWindowOptions={props.showChatWindowOptions}
                  />
                </Suspense>
              </div>
            }

            {
              uploadInputComp.includes(lastMessageType) &&
              <Suspense fallback={<Loading />}>
                <Upload showChatWindowOptions={props.showChatWindowOptions}
                 setShouldShowUpload={setShouldShowUpload}
                 shouldShowUpload={shouldShowUpload} />
              </Suspense>
            }

            {
              numberInputComp.includes(lastMessageType) &&
              <Suspense fallback={<Loading />}>
                <Number showChatWindowOptions={props.showChatWindowOptions} />
              </Suspense>
            }
            {
              textInputComp.includes(lastMessageType?.toLowerCase()) &&
              <Suspense fallback={<Loading />}>
                {
                ( environment.chatMenu?.voiceSupport?.isEnabled && environment.activeQuestion?.type?.toLowerCase() === 'ai'
                  && environment.activeInput === 'voice' )
                  ? <Audiov4></Audiov4>
                  : <Text message={lastMessageType} showChatWindowOptions={props.showChatWindowOptions} />
                }
              </Suspense>
            }
          </>
      }

      {/* Logic to handle internet connectivity UI */}
      {
        <div id='ele-375' className={`bounce-up flex flex-col items-center text-center bg-primaryBgColor fixed bottom-0 left-0 w-full p-4 rounded-t-3xl border-y border-customBorder2 ${internet === 'offline' ? 'block' : 'hidden'}`}>
          <div id='ele-376' className="bg-bpBGRed p-4 rounded-full"><img src={process.env.REACT_APP_CDN + "/offline.svg"} alt="" width={32} height={32} style={{ height: '32px', width: '32px' }} /></div>
          <div id='ele-377' className="font-mSemiBold text-xl p-2 mt-2">You're Offline!</div>
          <div id='ele-378' className="w-5/6 p-2 text-sm" style={{ color: '#666666' }}>Please check your internet connection. Your messages will be sent once the connection is restored.</div>
          <div id='ele-379' className="text-sm p-2"><PoweredBy /></div>
        </div>
      }


      <div id='ele-380' className={`absolute -top-20 py-2 px-3 rounded-xl fade-in ${internet === 'online' ? 'block' : 'hidden'}`} style={{ color: '#3DA144', background: '#D0F0D2' }}>
        You’re back online and can continue chatting. Your connection has been restored.
      </div>

    </div>
  )
}
